// Storage errors (S3/B2) come back as XML, which must not throw inside an
// XHR event handler and leave the promise pending forever. The start of the
// body is kept under `rawResponse` so failure reports can show the storage
// error code instead of an empty object.
const RAW_RESPONSE_MAX_LENGTH = 1000;

function parseXhrResponse(xhr: XMLHttpRequest) {
  try {
    return JSON.parse(xhr.responseText || "{}");
  } catch {
    return { rawResponse: xhr.responseText.slice(0, RAW_RESPONSE_MAX_LENGTH) };
  }
}

export function promisifyXhr(
  xhr: XMLHttpRequest,
  onProgress?: (progress: number, loaded: number, total: number) => any,
  stallTimeoutMs?: number
) {
  return new Promise((resolve, reject) => {
    // A dropped connection can leave the request half-open: no load, error
    // or abort ever fires, so callers would wait forever. When asked to, fail
    // it (not as a user abort) once it has been silent for stallTimeoutMs -
    // restarted on every upload progress event, so after the last byte it
    // becomes the wait for the response.
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const clearStallTimer = () => clearTimeout(stallTimer);
    const armStallTimer = () => {
      clearStallTimer();
      if (stallTimeoutMs === undefined) {
        return;
      }
      stallTimer = setTimeout(() => {
        reject({ status: 0, response: {}, isAbort: false });
        xhr.abort();
      }, stallTimeoutMs);
    };

    armStallTimer();
    if (onProgress || stallTimeoutMs !== undefined) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        const progress = Math.round((event.loaded * 100.0) / event.total);
        onProgress?.(progress, event.loaded, event.total);
        armStallTimer();
      };
    }
    xhr.onload = () => {
      clearStallTimer();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({
          status: xhr.status,
          response: parseXhrResponse(xhr),
          isAbort: false,
        });
      } else {
        reject({
          status: xhr.status,
          response: parseXhrResponse(xhr),
          isAbort: false,
        });
      }
    };
    xhr.onerror = () => {
      clearStallTimer();
      reject({
        status: xhr.status,
        response: parseXhrResponse(xhr),
        isAbort: false,
      });
    };
    xhr.onabort = () => {
      clearStallTimer();
      reject({
        status: xhr.status,
        response: parseXhrResponse(xhr),
        isAbort: true,
      });
    };
  });
}
